import React, { createContext, useState, useContext, useCallback } from 'react';
import services from '../services';

const ExamDataContext = createContext(null);

/**
 * Provider component that creates and manages shared exam data
 * This context stores exam types, systems, disciplines, and other
 * data that shouldn't be refetched multiple times
 */
export const ExamDataProvider = ({ children }) => {
  const [examTypes, setExamTypes] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch exam types on demand (no longer automatic)
   * Only fetches if data is not already loaded
   */
  const getExamTypes = useCallback(async () => {
    try {
      // Return cached data if available
      if (examTypes.length > 0) {
        return examTypes;
      }
      
      setLoading(true);
      console.log('📊 ExamDataContext: Fetching exam types...');
      
      console.log('📊 ExamDataContext: Calling API to fetch exam types...');
      const data = await services.examType.getExamTypes();
      console.log('📊 ExamDataContext: API response:', data);
      console.log('📊 ExamDataContext: Exam types received:', data?.length || 0);
      
      if (Array.isArray(data) && data.length > 0) {
        setExamTypes(data);
        return data;
      } else {
        console.error('📊 ExamDataContext: No exam types returned or invalid data format');
        setError('Failed to load exam data. Please try again.');
        return [];
      }
    } catch (err) {
      console.error('📊 ExamDataContext: Error fetching exam types:', err);
      setError('Failed to load exam data. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [examTypes]);
  /**
   * Get category groups for a specific exam type
   * Will fetch from API only if not already in memory
   */
  const getCategoryGroupsForExamType = async (examTypeId) => {
    try {
      // Check if we already have it cached
      if (categoryGroups[examTypeId]) {
        return categoryGroups[examTypeId];
      }
      
      console.log(`📊 ExamDataContext: Fetching category groups for exam type ${examTypeId}...`);
      const fetchedGroups = await services.categoryGroup.getCategoryGroups({ ExamTypeId: examTypeId });
      
      // Save to state
      setCategoryGroups(prev => ({
        ...prev,
        [examTypeId]: fetchedGroups
      }));
      
      return fetchedGroups;
    } catch (err) {
      console.error('📊 ExamDataContext: Error fetching category groups:', err);
      throw err;
    }
  };

  /**
   * Get categories for a specific group
   * Will fetch from API only if not already in memory
   */
  const getCategoriesForGroup = async (groupId, enabled = true) => {
    try {
      // Check if we already have it cached
      const cacheKey = `${groupId}-${enabled}`;
      if (categories[cacheKey]) {
        return categories[cacheKey];
      }
      
      console.log(`📊 ExamDataContext: Fetching categories for group ${groupId}...`);
      const fetchedCategories = await services.category.getCategories({ 
        CategoryGroupId: groupId, 
        Enabled: enabled
      });
      
      // Save to state
      setCategories(prev => ({
        ...prev,
        [cacheKey]: fetchedCategories
      }));
      
      return fetchedCategories;
    } catch (err) {
      console.error('📊 ExamDataContext: Error fetching categories:', err);
      throw err;
    }
  };
  // The value provided to consuming components
  const value = {
    examTypes,
    loading,
    error,
    getExamTypes,
    getCategoryGroupsForExamType,
    getCategoriesForGroup
  };

  return (
    <ExamDataContext.Provider value={value}>
      {children}
    </ExamDataContext.Provider>
  );
};

// Custom hook for using the exam data context
export const useExamData = () => {
  const context = useContext(ExamDataContext);
  if (!context) {
    throw new Error('useExamData must be used within an ExamDataProvider');
  }
  return context;
};
